import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auditApi, papersApi } from '../services/api';
import { AuditTask, Paper } from '../types';
import StatusBadge from '../components/StatusBadge';
import RiskBadge from '../components/RiskBadge';

function Tasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<AuditTask[]>([]);
  const [papersMap, setPapersMap] = useState<Record<string, Paper>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await auditApi.listTasks();
      const tasksData = response.data;
      setTasks(tasksData);
      setError('');

      // Fetch paper details for each unique paper_id
      const uniquePaperIds = [...new Set(tasksData.map((t: AuditTask) => t.paper_id))] as string[];
      const papersRes = await Promise.all(
        uniquePaperIds.map(async (id: string) => {
          try {
            const res = await papersApi.get(id);
            return res.data;
          } catch {
            return null;
          }
        })
      );
      const map: Record<string, Paper> = {};
      papersRes.forEach((p) => {
        if (p) map[p.id] = p;
      });
      setPapersMap(map);
    } catch (err: any) {
      if (err.response) {
        setError('获取任务列表失败');
      }
      // Ignore network errors (e.g., API restarting)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();

    // Poll for updates every 5 seconds
    intervalRef.current = setInterval(fetchTasks, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchTasks]);

  // Stop polling when all tasks are completed or failed
  useEffect(() => {
    if (tasks.length > 0 && tasks.every((t) => t.status === 'completed' || t.status === 'failed')) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [tasks]);

  const handleViewReport = (taskId: string) => {
    navigate(`/report/${taskId}`);
  };

  const getTaskTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      numeric_forensics: '数值取证',
      image_integrity: '图像完整性',
      citation_check: '引用检查',
      package_completeness: '包完整性',
      geng_numeric: 'Geng数值筛查',
      flow_plot: '流式图检测',
      full_audit: '完整审查',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            审查任务 ({tasks.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">加载中...</div>
        ) : tasks.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            暂无审查任务，请先上传论文并创建审查
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <p className="text-sm font-medium text-gray-900">
                        任务 #{task.id.slice(0, 8)}
                      </p>
                      <span className="text-sm text-gray-500">
                        {getTaskTypeLabel(task.task_type)}
                      </span>
                      {papersMap[task.paper_id] && (
                        <Link
                          to="/"
                          className="text-sm text-blue-600 hover:text-blue-800 truncate max-w-xs"
                          title={papersMap[task.paper_id].title || papersMap[task.paper_id].original_filename}
                        >
                          📄 {papersMap[task.paper_id].title || papersMap[task.paper_id].original_filename}
                        </Link>
                      )}
                    </div>

                    <div className="flex items-center space-x-4 mt-1">
                      <StatusBadge status={task.status} />

                      {task.overall_risk_level && (
                        <RiskBadge level={task.overall_risk_level} size="sm" />
                      )}

                      {task.findings_count > 0 && (
                        <span className="text-sm text-gray-500">
                          {task.findings_count} 个发现
                          {task.high_findings > 0 && (
                            <span className="text-red-600 ml-1">
                              ({task.high_findings} 高风险)
                            </span>
                          )}
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-gray-400 mt-1">
                      创建于 {new Date(task.created_at).toLocaleString()}
                      {task.completed_at &&
                        ` | 完成于 ${new Date(task.completed_at).toLocaleString()}`}
                    </p>
                  </div>

                  <div className="ml-4">
                    {task.status === 'completed' && (
                      <button
                        onClick={() => handleViewReport(task.id)}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                      >
                        查看报告
                      </button>
                    )}

                    {task.status === 'running' && (
                      <div className="flex items-center text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        处理中...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Tasks;
